import { useEffect, useState } from 'react';
import { api, ErrorApi } from '../api/cliente.js';
import { useToast } from '../componentes/Toast.js';
import { createPortal } from 'react-dom';

interface UsuarioPortal {
  id: string;
  nombre: string;
  email: string;
  rol: 'RRHH' | 'SERVICIO_MEDICO';
  activo: number;
  creadoEn: string;
}

interface Empresa {
  id: string;
  nombre: string;
  cuit: string | null;
  creadoEn: string;
  usuarios: UsuarioPortal[];
}

export function Empresas() {
  const [empresas, setEmpresas] = useState<Empresa[] | null>(null);
  const [error, setError] = useState('');
  const [modalEmpresaAbierto, setModalEmpresaAbierto] = useState(false);
  const [modalUsuarioAbierto, setModalUsuarioAbierto] = useState(false);
  const [seleccionadaEmpresa, setSeleccionadaEmpresa] = useState<Empresa | null>(null);
  const toast = useToast();

  // Formulario Editar Usuario Portal
  const [usuarioEditando, setUsuarioEditando] = useState<UsuarioPortal | null>(null);
  const [editNombre, setEditNombre] = useState('');
  const [editEmail, setEditEmail] = useState('');
  const [editRol, setEditRol] = useState<'RRHH' | 'SERVICIO_MEDICO'>('RRHH');
  const [editPassword, setEditPassword] = useState('');
  const [editPasswordConfirmar, setEditPasswordConfirmar] = useState('');
  const [guardandoEdicion, setGuardandoEdicion] = useState(false);
  const [errorEdicionForm, setErrorEdicionForm] = useState('');

  // Formulario Empresa
  const [empresaNombre, setEmpresaNombre] = useState('');
  const [empresaCuit, setEmpresaCuit] = useState('');
  const [creandoEmpresa, setCreandoEmpresa] = useState(false);
  const [errorEmpresaForm, setErrorEmpresaForm] = useState('');

  // Formulario Usuario Portal
  const [usuarioNombre, setUsuarioNombre] = useState('');
  const [usuarioEmail, setUsuarioEmail] = useState('');
  const [usuarioPassword, setUsuarioPassword] = useState('');
  const [usuarioPasswordConfirmar, setUsuarioPasswordConfirmar] = useState('');
  const [usuarioRol, setUsuarioRol] = useState<'RRHH' | 'SERVICIO_MEDICO'>('RRHH');
  const [creandoUsuario, setCreandoUsuario] = useState(false);
  const [errorUsuarioForm, setErrorUsuarioForm] = useState('');

  async function cargarEmpresas() {
    try {
      setEmpresas(await api.get<Empresa[]>('/api/operador/empresas'));
    } catch (err) {
      setError(err instanceof ErrorApi ? err.message : 'No se pudieron cargar las empresas');
    }
  }

  useEffect(() => {
    void cargarEmpresas();
  }, []);

  async function crearEmpresa(e: React.FormEvent) {
    e.preventDefault();
    if (!empresaNombre) {
      setErrorEmpresaForm('El nombre de la empresa es obligatorio.');
      return;
    }
    setCreandoEmpresa(true);
    setErrorEmpresaForm('');

    try {
      await api.post('/api/operador/empresas', {
        nombre: empresaNombre,
        cuit: empresaCuit || undefined,
      });

      toast.mostrar(`Empresa "${empresaNombre}" registrada correctamente.`);
      setEmpresaNombre('');
      setEmpresaCuit('');
      setModalEmpresaAbierto(false);
      await cargarEmpresas();
    } catch (err) {
      setErrorEmpresaForm(err instanceof ErrorApi ? err.message : 'Error al registrar la empresa');
    } finally {
      setCreandoEmpresa(false);
    }
  }

  async function crearUsuario(e: React.FormEvent) {
    e.preventDefault();
    if (!seleccionadaEmpresa) return;
    if (!usuarioNombre || !usuarioEmail || !usuarioPassword) {
      setErrorUsuarioForm('Por favor completa todos los campos.');
      return;
    }
    if (usuarioPassword !== usuarioPasswordConfirmar) {
      setErrorUsuarioForm('Las contraseñas no coinciden.');
      return;
    }
    setCreandoUsuario(true);
    setErrorUsuarioForm('');

    try {
      await api.post(`/api/operador/empresas/${seleccionadaEmpresa.id}/usuarios`, {
        nombre: usuarioNombre,
        email: usuarioEmail,
        password: usuarioPassword,
        rol: usuarioRol,
      });

      toast.mostrar(`Usuario "${usuarioNombre}" creado para ${seleccionadaEmpresa.nombre}.`);
      setUsuarioNombre('');
      setUsuarioEmail('');
      setUsuarioPassword('');
      setUsuarioPasswordConfirmar('');
      setUsuarioRol('RRHH');
      setModalUsuarioAbierto(false);
      setSeleccionadaEmpresa(null);
      await cargarEmpresas();
    } catch (err) {
      setErrorUsuarioForm(err instanceof ErrorApi ? err.message : 'Error al crear el usuario');
    } finally {
      setCreandoUsuario(false);
    }
  }

  function abrirModalUsuario(emp: Empresa) {
    setSeleccionadaEmpresa(emp);
    setErrorUsuarioForm('');
    setModalUsuarioAbierto(true);
  }

  function cerrarModalEmpresa() {
    setModalEmpresaAbierto(false);
    setEmpresaNombre('');
    setEmpresaCuit('');
    setErrorEmpresaForm('');
  }

  function cerrarModalUsuario() {
    setModalUsuarioAbierto(false);
    setUsuarioNombre('');
    setUsuarioEmail('');
    setUsuarioPassword('');
    setUsuarioPasswordConfirmar('');
    setUsuarioRol('RRHH');
    setErrorUsuarioForm('');
    setSeleccionadaEmpresa(null);
  }

  function abrirModalEditarUsuario(usr: UsuarioPortal) {
    setUsuarioEditando(usr);
    setEditNombre(usr.nombre);
    setEditEmail(usr.email);
    setEditRol(usr.rol);
    setEditPassword('');
    setEditPasswordConfirmar('');
    setErrorEdicionForm('');
  }

  function cerrarModalEditarUsuario() {
    setUsuarioEditando(null);
    setEditNombre('');
    setEditEmail('');
    setEditRol('RRHH');
    setEditPassword('');
    setEditPasswordConfirmar('');
    setErrorEdicionForm('');
  }

  async function guardarEdicionUsuario(e: React.FormEvent) {
    e.preventDefault();
    if (!usuarioEditando) return;
    if (!editNombre || !editEmail) {
      setErrorEdicionForm('Nombre y correo son obligatorios.');
      return;
    }
    if (editPassword && editPassword.length < 10) {
      setErrorEdicionForm('La nueva contraseña debe tener al menos 10 caracteres.');
      return;
    }
    if (editPassword !== editPasswordConfirmar) {
      setErrorEdicionForm('Las contraseñas no coinciden.');
      return;
    }
    setGuardandoEdicion(true);
    setErrorEdicionForm('');
    try {
      await api.put(`/api/operador/empresas/usuarios/${usuarioEditando.id}`, {
        nombre: editNombre,
        email: editEmail,
        rol: editRol,
        ...(editPassword ? { password: editPassword } : {}),
      });
      toast.mostrar(`Usuario "${editNombre}" actualizado correctamente.`);
      cerrarModalEditarUsuario();
      await cargarEmpresas();
    } catch (err) {
      setErrorEdicionForm(err instanceof ErrorApi ? err.message : 'Error al actualizar el usuario');
    } finally {
      setGuardandoEdicion(false);
    }
  }

  async function toggleActivoUsuario(usuarioId: string) {
    try {
      await api.post(`/api/operador/empresas/usuarios/${usuarioId}/toggle-activo`, {});
      toast.mostrar('Estado del usuario del portal actualizado.');
      await cargarEmpresas();
    } catch (err) {
      alert(err instanceof ErrorApi ? err.message : 'Error al cambiar estado del usuario');
    }
  }

  return (
    <section style={{ animation: 'fadeInUp 0.4s ease forwards' }}>
      <div className="fila" style={{ marginBottom: 20, alignItems: 'center' }}>
        <div>
          <h2>🏢 Empresas y Usuarios de Portal</h2>
          <p className="muted">
            Gestioná los perfiles de las empresas clientes y creá usuarios autorizados (RRHH o Servicio Médico) para que registren ausencias.
          </p>
        </div>
        <button className="primario" style={{ width: 'auto' }} onClick={() => setModalEmpresaAbierto(true)}>
          + Registrar Empresa
        </button>
      </div>

      {error && <div className="error-box">{error}</div>}
      {empresas === null ? (
        <div className="cargando">Cargando empresas y perfiles…</div>
      ) : empresas.length === 0 ? (
        <p className="muted">No hay empresas registradas.</p>
      ) : (
        <div className="grid-tarjetas">
          {empresas.map((emp) => (
            <div key={emp.id} className="tarjeta glass" style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', padding: 20 }}>
              <div>
                <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8, marginBottom: 12 }}>
                  <div style={{ minWidth: 0, flex: '1 1 160px' }}>
                    <h3 style={{ margin: 0, fontSize: 16, color: 'var(--texto)', wordBreak: 'break-word' }}>{emp.nombre}</h3>
                    <div className="mini muted" style={{ marginTop: 2, wordBreak: 'break-word' }}>
                      CUIT: {emp.cuit || '—'} · ID: {emp.id}
                    </div>
                  </div>
                  <button className="secundario mini" onClick={() => abrirModalUsuario(emp)} style={{ width: 'auto', flexShrink: 0 }}>
                    + Crear Usuario
                  </button>
                </div>

                <div style={{ borderTop: '1px solid var(--borde)', paddingTop: 12, marginTop: 12 }}>
                  <span className="mini" style={{ fontWeight: 600, display: 'block', marginBottom: 8 }}>
                    Usuarios del Portal ({emp.usuarios.length}):
                  </span>
                  {emp.usuarios.length === 0 ? (
                    <p className="mini muted" style={{ margin: 0 }}>No hay usuarios creados para esta empresa.</p>
                  ) : (
                    <ul style={{ paddingLeft: 0, listStyle: 'none', margin: 0 }}>
                      {emp.usuarios.map((usr) => (
                        <li key={usr.id} className="mini" style={{ marginBottom: 8, display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                          <div style={{ minWidth: 0, wordBreak: 'break-word' }}>
                            <strong>{usr.nombre}</strong> <span className="muted">({usr.email})</span>{' '}
                            <span className={`insignia ${usr.rol === 'SERVICIO_MEDICO' ? 'verde' : 'azul'}`} style={{ fontSize: 9, padding: '2px 4px', display: 'inline-block', marginLeft: 4 }}>
                              {usr.rol === 'SERVICIO_MEDICO' ? 'Med' : 'RRHH'}
                            </span>
                            <span className={`insignia ${usr.activo === 1 ? 'verde' : 'roja'}`} style={{ fontSize: 9, padding: '2px 4px', display: 'inline-block', marginLeft: 4 }}>
                              {usr.activo === 1 ? 'Activo' : 'Inactivo'}
                            </span>
                          </div>
                          <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                            <button
                              type="button"
                              className="secundario mini"
                              onClick={() => abrirModalEditarUsuario(usr)}
                              style={{ padding: '2px 6px', fontSize: 10, width: 'auto' }}
                            >
                              Editar
                            </button>
                            <button
                              type="button"
                              className="secundario mini"
                              onClick={() => toggleActivoUsuario(usr.id)}
                              style={{ padding: '2px 6px', fontSize: 10, width: 'auto' }}
                            >
                              {usr.activo === 1 ? 'Desactivar' : 'Activar'}
                            </button>
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal / Formulario Nueva Empresa */}
      {modalEmpresaAbierto && createPortal(
        <div className="capa-bloqueo" onClick={(e) => { if (e.target === e.currentTarget) cerrarModalEmpresa(); }}>
          <div className="dialogo glass">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid var(--borde)', paddingBottom: 12, marginBottom: 16 }}>
              <h3 style={{ margin: 0, fontSize: 18 }}>Registrar Nueva Empresa</h3>
              <button type="button" className="fantasma" onClick={cerrarModalEmpresa} style={{ fontSize: 20, padding: 4, lineHeight: 1 }}>✕</button>
            </div>
            
            {errorEmpresaForm && <div className="error-box">{errorEmpresaForm}</div>}

            <form onSubmit={crearEmpresa}>
              <div className="campo" style={{ marginBottom: 12 }}>
                <label htmlFor="empresaNombre">Nombre de la Empresa (*)</label>
                <input id="empresaNombre" type="text" required value={empresaNombre} onChange={(e) => setEmpresaNombre(e.target.value)} placeholder="Ej. Toro Bueno S.A." />
              </div>

              <div className="campo" style={{ marginBottom: 20 }}>
                <label htmlFor="empresaCuit">CUIT (Opcional)</label>
                <input id="empresaCuit" type="text" value={empresaCuit} onChange={(e) => setEmpresaCuit(e.target.value)} placeholder="Ej. 30-12345678-9" />
              </div>

              <div className="fila" style={{ gap: 12, justifyContent: 'flex-end', marginTop: 16 }}>
                <button type="button" className="secundario" onClick={cerrarModalEmpresa} disabled={creandoEmpresa}>
                  Cancelar
                </button>
                <button type="submit" className="primario" disabled={creandoEmpresa}>
                  {creandoEmpresa ? 'Registrando…' : 'Registrar Empresa'}
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}

      {/* Modal / Formulario Nuevo Usuario */}
      {modalUsuarioAbierto && seleccionadaEmpresa && createPortal(
        <div className="capa-bloqueo" onClick={(e) => { if (e.target === e.currentTarget) cerrarModalUsuario(); }}>
          <div className="dialogo glass">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid var(--borde)', paddingBottom: 12, marginBottom: 16 }}>
              <h3 style={{ margin: 0, fontSize: 18 }}>Nuevo Usuario: {seleccionadaEmpresa.nombre}</h3>
              <button type="button" className="fantasma" onClick={cerrarModalUsuario} style={{ fontSize: 20, padding: 4, lineHeight: 1 }}>✕</button>
            </div>
            
            {errorUsuarioForm && <div className="error-box">{errorUsuarioForm}</div>}

            <form onSubmit={crearUsuario}>
              <div className="campo" style={{ marginBottom: 12 }}>
                <label htmlFor="usuarioNombre">Nombre Completo (*)</label>
                <input id="usuarioNombre" type="text" required value={usuarioNombre} onChange={(e) => setUsuarioNombre(e.target.value)} placeholder="Ej. Maria Lopez" />
              </div>

              <div className="campo" style={{ marginBottom: 12 }}>
                <label htmlFor="usuarioEmail">Correo Electrónico (*)</label>
                <input id="usuarioEmail" type="email" required value={usuarioEmail} onChange={(e) => setUsuarioEmail(e.target.value)} placeholder="usuario@empresa.com" />
              </div>

              <div className="campo" style={{ marginBottom: 12 }}>
                <label htmlFor="usuarioPassword">Contraseña (*)</label>
                <input id="usuarioPassword" type="password" required value={usuarioPassword} onChange={(e) => setUsuarioPassword(e.target.value)} placeholder="Mínimo 6 caracteres" />
              </div>

              <div className="campo" style={{ marginBottom: 12 }}>
                <label htmlFor="usuarioPasswordConfirmar">Confirmar Contraseña (*)</label>
                <input id="usuarioPasswordConfirmar" type="password" required value={usuarioPasswordConfirmar} onChange={(e) => setUsuarioPasswordConfirmar(e.target.value)} placeholder="Repetí la contraseña" />
              </div>

              <div className="campo" style={{ marginBottom: 20 }}>
                <label htmlFor="usuarioRol">Rol en el Portal (*)</label>
                <select id="usuarioRol" value={usuarioRol} onChange={(e) => setUsuarioRol(e.target.value as 'RRHH' | 'SERVICIO_MEDICO')}>
                  <option value="RRHH">👥 Recursos Humanos (Ve constancia laboral sin diagnóstico)</option>
                  <option value="SERVICIO_MEDICO">🩺 Servicio Médico (Ve constancia clínica + observaciones)</option>
                </select>
              </div>

              <div className="fila" style={{ gap: 12, justifyContent: 'flex-end', marginTop: 16 }}>
                <button type="button" className="secundario" onClick={cerrarModalUsuario} disabled={creandoUsuario}>
                  Cancelar
                </button>
                <button type="submit" className="primario" disabled={creandoUsuario}>
                  {creandoUsuario ? 'Creando…' : 'Crear Usuario'}
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}

      {/* Modal / Formulario Editar Usuario */}
      {usuarioEditando && createPortal(
        <div className="capa-bloqueo" onClick={(e) => { if (e.target === e.currentTarget) cerrarModalEditarUsuario(); }}>
          <div className="dialogo glass">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid var(--borde)', paddingBottom: 12, marginBottom: 16 }}>
              <h3 style={{ margin: 0, fontSize: 18 }}>Editar Usuario: {usuarioEditando.nombre}</h3>
              <button type="button" className="fantasma" onClick={cerrarModalEditarUsuario} style={{ fontSize: 20, padding: 4, lineHeight: 1 }}>✕</button>
            </div>

            {errorEdicionForm && <div className="error-box">{errorEdicionForm}</div>}

            <form onSubmit={(e) => void guardarEdicionUsuario(e)}>
              <div className="campo" style={{ marginBottom: 12 }}>
                <label htmlFor="editNombre">Nombre Completo (*)</label>
                <input id="editNombre" type="text" required value={editNombre} onChange={(e) => setEditNombre(e.target.value)} />
              </div>

              <div className="campo" style={{ marginBottom: 12 }}>
                <label htmlFor="editEmail">Correo Electrónico (*)</label>
                <input id="editEmail" type="email" required value={editEmail} onChange={(e) => setEditEmail(e.target.value)} />
              </div>

              <div className="campo" style={{ marginBottom: 12 }}>
                <label htmlFor="editPassword">Nueva Contraseña (opcional)</label>
                <input id="editPassword" type="password" value={editPassword} onChange={(e) => setEditPassword(e.target.value)} placeholder="Dejar en blanco para no cambiarla" />
              </div>

              {editPassword && (
                <div className="campo" style={{ marginBottom: 12 }}>
                  <label htmlFor="editPasswordConfirmar">Confirmar Nueva Contraseña (*)</label>
                  <input id="editPasswordConfirmar" type="password" required value={editPasswordConfirmar} onChange={(e) => setEditPasswordConfirmar(e.target.value)} placeholder="Repetí la nueva contraseña" />
                </div>
              )}

              <div className="campo" style={{ marginBottom: 20 }}>
                <label htmlFor="editRol">Rol en el Portal (*)</label>
                <select id="editRol" value={editRol} onChange={(e) => setEditRol(e.target.value as 'RRHH' | 'SERVICIO_MEDICO')}>
                  <option value="RRHH">👥 Recursos Humanos (Ve constancia laboral sin diagnóstico)</option>
                  <option value="SERVICIO_MEDICO">🩺 Servicio Médico (Ve constancia clínica + observaciones)</option>
                </select>
              </div>

              <div className="fila" style={{ gap: 12, justifyContent: 'flex-end', marginTop: 16 }}>
                <button type="button" className="secundario" onClick={cerrarModalEditarUsuario} disabled={guardandoEdicion}>
                  Cancelar
                </button>
                <button type="submit" className="primario" disabled={guardandoEdicion}>
                  {guardandoEdicion ? 'Guardando…' : 'Guardar Cambios'}
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}
    </section>
  );
}
